import type { Provider, ProviderType, ServiceRoute } from '../types'

export type CloudVendor = 'aws' | 'azure' | 'google' | 'cloudflare' | 'oracle' | 'alibaba' | 'local' | 'other'

export interface ProviderPreset {
  kind: string
  vendor: CloudVendor
  name: string
  description: string
  unit: string
  endpoint?: string
  region?: string
  priority: number
  usageMode: 'manual' | 'http' | 'reported'
  authHeader?: string
  authPrefix?: string
  gatewayEnabled?: boolean
  resetPeriod?: 'daily' | 'monthly' | 'yearly' | 'none'
  metric?: string
}

export const providerPresets: ProviderPreset[] = [
  {
    kind: 'aws_s3', vendor: 'aws', name: 'AWS S3 免费层', description: '对象存储、静态资源和分发源。', unit: 'GB',
    endpoint: 'https://s3.amazonaws.com', region: 'eu-west-2', priority: 30, usageMode: 'manual', resetPeriod: 'monthly',
  },
  {
    kind: 'cloudflare_r2', vendor: 'cloudflare', name: 'Cloudflare R2 公共静态资源', description: 'R2 对象存储、静态资源与零出口流量费分发源。', unit: 'GB',
    region: 'auto', priority: 20, usageMode: 'manual', gatewayEnabled: false, resetPeriod: 'monthly', metric: 'storage_gb_month',
  },
  {
    kind: 'aws_polly', vendor: 'aws', name: 'AWS Polly 语音额度', description: '按字符跟踪文本转语音用量。', unit: 'chars',
    endpoint: 'https://polly.eu-west-2.amazonaws.com', region: 'eu-west-2', priority: 20, usageMode: 'reported', resetPeriod: 'monthly', metric: 'synthesized_characters',
  },
  {
    kind: 'aws_transcribe', vendor: 'aws', name: 'AWS Transcribe 转写额度', description: '按分钟跟踪语音转文本调用。', unit: 'minutes',
    endpoint: 'https://transcribe.eu-west-2.amazonaws.com', region: 'eu-west-2', priority: 20, usageMode: 'reported', resetPeriod: 'monthly', metric: 'transcribed_minutes',
  },
  {
    kind: 'aws_bedrock', vendor: 'aws', name: 'AWS Bedrock 试用预算', description: '按金额或 Token 跟踪模型调用。', unit: 'USD',
    endpoint: 'https://bedrock-runtime.eu-west-2.amazonaws.com', region: 'eu-west-2', priority: 20, usageMode: 'reported', resetPeriod: 'monthly', metric: 'model_budget',
  },
  {
    kind: 'azure_tts', vendor: 'azure', name: 'Azure Speech TTS', description: '按字符跟踪 Azure 语音合成。', unit: 'chars',
    endpoint: 'https://uksouth.tts.speech.microsoft.com/cognitiveservices', region: 'uksouth', priority: 10, usageMode: 'reported', authHeader: 'Ocp-Apim-Subscription-Key', authPrefix: '', gatewayEnabled: true, resetPeriod: 'monthly', metric: 'tts_characters',
  },
  {
    kind: 'azure_speech_stt', vendor: 'azure', name: 'Azure Speech 转文本', description: '按音频分钟跟踪语音识别。', unit: 'minutes',
    endpoint: 'https://uksouth.stt.speech.microsoft.com', region: 'uksouth', priority: 10, usageMode: 'reported', authHeader: 'Ocp-Apim-Subscription-Key', authPrefix: '', gatewayEnabled: true, resetPeriod: 'monthly', metric: 'stt_minutes',
  },
  {
    kind: 'azure_ai_search', vendor: 'azure', name: 'Azure AI Search 免费资源', description: '跟踪搜索请求或自定义查询额度。', unit: 'requests',
    endpoint: 'https://YOUR-SERVICE.search.windows.net', region: 'uksouth', priority: 10, usageMode: 'reported', authHeader: 'api-key', authPrefix: '', gatewayEnabled: true, resetPeriod: 'monthly', metric: 'search_queries',
  },
  {
    kind: 'azure_openai', vendor: 'azure', name: 'Azure OpenAI 资源', description: '以 Token 或账户预算管理模型调用。', unit: 'tokens',
    endpoint: 'https://YOUR-RESOURCE.openai.azure.com', region: 'uksouth', priority: 15, usageMode: 'reported', authHeader: 'api-key', authPrefix: '', gatewayEnabled: true, resetPeriod: 'monthly', metric: 'tokens',
  },
  {
    kind: 'google_vertex_ai', vendor: 'google', name: 'Google Cloud AI 试用金', description: '按金额跟踪 Vertex AI / Gemini 试用预算。', unit: 'USD',
    endpoint: 'https://aiplatform.googleapis.com', region: 'europe-west2', priority: 10, usageMode: 'manual', authHeader: 'Authorization', authPrefix: 'Bearer', gatewayEnabled: false, resetPeriod: 'none', metric: 'trial_credit',
  },
  {
    kind: 'google_custom_search', vendor: 'google', name: 'Google Search API', description: '按日跟踪搜索 API 请求数。', unit: 'requests',
    endpoint: 'https://customsearch.googleapis.com/customsearch/v1', priority: 10, usageMode: 'reported', authHeader: 'X-Goog-Api-Key', authPrefix: '', gatewayEnabled: true, resetPeriod: 'daily', metric: 'search_requests',
  },
  {
    kind: 'google_cloud_tts', vendor: 'google', name: 'Google Cloud Text-to-Speech', description: '按字符跟踪语音合成。', unit: 'chars',
    endpoint: 'https://texttospeech.googleapis.com/v1', priority: 15, usageMode: 'reported', authHeader: 'Authorization', authPrefix: 'Bearer', gatewayEnabled: true, resetPeriod: 'monthly', metric: 'synthesized_characters',
  },
  {
    kind: 'google_speech_to_text', vendor: 'google', name: 'Google Cloud Speech-to-Text', description: '按分钟跟踪语音识别。', unit: 'minutes',
    endpoint: 'https://speech.googleapis.com/v2', priority: 15, usageMode: 'reported', authHeader: 'Authorization', authPrefix: 'Bearer', gatewayEnabled: true, resetPeriod: 'monthly', metric: 'recognized_minutes',
  },
  {
    kind: 'google_drive', vendor: 'google', name: 'Google Drive map（只读）', description: '通过 Rclone 把现有 Google Drive 挂载到固定的 /map 路径。', unit: 'GB',
    endpoint: 'file:///map', priority: 60, usageMode: 'manual', resetPeriod: 'none',
  },
  {
    kind: 'oracle_oss', vendor: 'oracle', name: 'Oracle Object Storage', description: '对象存储容量、流量与请求量。', unit: 'GB',
    endpoint: 'https://objectstorage.REGION.oraclecloud.com', priority: 25, usageMode: 'manual', resetPeriod: 'monthly',
  },
  {
    kind: 'oracle_compute', vendor: 'oracle', name: 'Oracle Compute', description: '跟踪实例时长、OCPU 或试用预算。', unit: 'hours',
    priority: 25, usageMode: 'manual', resetPeriod: 'monthly',
  },
  {
    kind: 'oracle_autonomous_db', vendor: 'oracle', name: 'Oracle Autonomous Database', description: '跟踪数据库容量、OCPU 时长或试用预算。', unit: 'OCPU-hours',
    priority: 25, usageMode: 'manual', resetPeriod: 'monthly',
  },
  {
    kind: 'alibaba_ecs', vendor: 'alibaba', name: 'Alibaba Cloud ECS（国际站）', description: '跟踪 ECS 试用实例时长或预算。', unit: 'hours',
    priority: 25, usageMode: 'manual', resetPeriod: 'monthly',
  },
  {
    kind: 'alibaba_oss', vendor: 'alibaba', name: 'Alibaba Cloud OSS（国际站）', description: '跟踪 OSS 容量、流量与请求额度。', unit: 'GB',
    endpoint: 'https://oss-REGION.aliyuncs.com', priority: 25, usageMode: 'manual', resetPeriod: 'monthly',
  },
  {
    kind: 'alibaba_function_compute', vendor: 'alibaba', name: 'Alibaba Cloud Function Compute', description: '跟踪函数请求、执行时长或试用预算。', unit: 'requests',
    priority: 25, usageMode: 'manual', resetPeriod: 'monthly',
  },
  {
    kind: 'alibaba_model_studio', vendor: 'alibaba', name: 'Alibaba Cloud Model Studio', description: '按 Token 或试用预算管理模型调用。', unit: 'tokens',
    endpoint: 'https://dashscope-intl.aliyuncs.com/compatible-mode/v1', priority: 20, usageMode: 'reported', authHeader: 'Authorization', authPrefix: 'Bearer', gatewayEnabled: true, resetPeriod: 'monthly', metric: 'tokens',
  },
  {
    kind: 'ollama', vendor: 'local', name: '本地 Ollama 保底', description: '云模型不可用时自动由服务器接管。', unit: 'unlimited',
    endpoint: 'http://127.0.0.1:11434', priority: 1000, usageMode: 'reported', gatewayEnabled: true, resetPeriod: 'none',
  },
  {
    kind: 'local_tts', vendor: 'local', name: '本地 TTS 保底', description: '接入 GPT-SoVITS、Piper 或自建语音服务。', unit: 'unlimited',
    endpoint: 'http://127.0.0.1:9880', priority: 1000, usageMode: 'reported', gatewayEnabled: true, resetPeriod: 'none',
  },
  {
    kind: 'local_stt', vendor: 'local', name: '本地语音转文本保底', description: '接入 Whisper 或自建语音识别服务。', unit: 'unlimited',
    endpoint: 'http://127.0.0.1:9000', priority: 1000, usageMode: 'reported', gatewayEnabled: true, resetPeriod: 'none',
  },
  {
    kind: 'local_search', vendor: 'local', name: '本地搜索保底', description: '接入 SearXNG 或自建检索服务。', unit: 'unlimited',
    endpoint: 'http://127.0.0.1:8081', priority: 1000, usageMode: 'reported', gatewayEnabled: true, resetPeriod: 'none',
  },
]

export function presetForKind(kind: string): ProviderPreset | undefined {
  return providerPresets.find((item) => item.kind === kind)
}

export function vendorForKind(kind = ''): CloudVendor {
  const preset = presetForKind(kind)
  if (preset) return preset.vendor
  const normalized = kind.toLowerCase()
  if (normalized === 'cloudflare_r2' || normalized.startsWith('r2_')) return 'cloudflare'
  if (normalized.startsWith('aws_') || normalized.includes('amazon')) return 'aws'
  if (normalized.startsWith('azure_') || normalized.includes('onedrive')) return 'azure'
  if (normalized.startsWith('google_') || normalized.includes('gcp') || normalized.includes('gemini') || normalized.includes('vertex')) return 'google'
  if (normalized.startsWith('oracle_')) return 'oracle'
  if (normalized.startsWith('alibaba_') || normalized.startsWith('aliyun_') || normalized.includes('dashscope')) return 'alibaba'
  if (normalized.startsWith('local_') || normalized === 'ollama' || normalized === 'antigravity') return 'local'
  return 'other'
}

export function vendorForProvider(provider: Pick<Provider, 'kind' | 'config'>): CloudVendor {
  const configured = provider.config?.vendor as CloudVendor | undefined
  return configured || vendorForKind(provider.kind)
}

export function vendorForType(type?: ProviderType): CloudVendor {
  return (type?.vendor as CloudVendor | undefined) || vendorForKind(type?.kind || '')
}

export function quotaPercent(provider: Pick<Provider, 'quota'>): number {
  if (!provider.quota || provider.quota.limit <= 0) return 0
  return Math.max(0, (provider.quota.used / provider.quota.limit) * 100)
}

export function remainingPercent(provider: Pick<Provider, 'quota'>): number {
  if (!provider.quota || provider.quota.limit <= 0) return 100
  return Math.max(0, Math.min(100, 100 - quotaPercent(provider)))
}

export function remainingAmount(provider: Pick<Provider, 'quota'>): number {
  if (!provider.quota || provider.quota.limit <= 0) return Number.POSITIVE_INFINITY
  return Math.max(0, provider.quota.limit - provider.quota.used)
}

export function dateExpired(value?: string): boolean {
  if (!value) return false
  const time = new Date(value).getTime()
  return Number.isFinite(time) && time <= Date.now()
}

export function providerExpired(provider: Pick<Provider, 'config' | 'status'>): boolean {
  return provider.status === 'expired' || dateExpired(provider.config?.expires_at)
}

export function providerAvailable(provider: Provider): boolean {
  if (!provider.enabled || providerExpired(provider)) return false
  if (['error', 'quota_exhausted', 'needs_config', 'disabled', 'expired'].includes(provider.status)) return false
  if (provider.quota.limit > 0 && provider.quota.hard_stop_percent > 0 && quotaPercent(provider) >= provider.quota.hard_stop_percent) return false
  return true
}

export function selectRouteProvider(route: ServiceRoute, providers: Provider[]): Provider | undefined {
  const candidates = route.provider_ids
    .filter((id) => id !== route.fallback_provider_id)
    .map((id) => providers.find((provider) => provider.id === id))
    .filter((provider): provider is Provider => Boolean(provider) && providerAvailable(provider as Provider))
  const selected = [...candidates].sort((a, b) => {
    if (route.strategy === 'lowest_usage') return quotaPercent(a) - quotaPercent(b) || a.priority - b.priority
    return a.priority - b.priority || quotaPercent(a) - quotaPercent(b)
  })[0]
  if (selected) return selected
  return providers.find((provider) => provider.id === route.fallback_provider_id && providerAvailable(provider))
}

export function isLocalProvider(provider?: Pick<Provider, 'kind' | 'config'>): boolean {
  return Boolean(provider && vendorForProvider(provider) === 'local')
}

export function toDatetimeLocal(value?: string): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const offset = date.getTimezoneOffset() * 60_000
  return new Date(date.getTime() - offset).toISOString().slice(0, 16)
}

export function toISOStringOrEmpty(value: string): string {
  if (!value.trim()) return ''
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value.trim() : date.toISOString()
}
